import React from 'react';

// ── Icons ─────────────────────────────────────────────────────────────────
const ClockIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

// ── Interfaces ────────────────────────────────────────────────────────────
interface LogEntry {
  id: number;
  createdAt: string;
  utenteName: string;
  azione: string;
  dettaglio?: string;
}

interface TabLogProps {
  log: LogEntry[];
}

// ── Componente ────────────────────────────────────────────────────────────
export default function TabLog({ log }: TabLogProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ora';
    if (diffMins < 60) return `${diffMins} minuti fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;

    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAzioneBadgeClass = (azione: string): string => {
    const lower = azione.toLowerCase();

    // Creazione → verde
    if (lower.includes('creaz')) return 'anagrafiche-badge-log anagrafiche-badge-log-creazione';

    // Modifiche → blu
    if (lower.includes('modifica dati') || lower.includes('modifica cliente') ||
        lower.includes('modifica fornitore') || lower.includes('modifica indirizzo') ||
        lower.includes('modifica email') || lower.includes('modifica contatto') ||
        lower.includes('modifica filiale')) {
      return 'anagrafiche-badge-log anagrafiche-badge-log-modifica';
    }

    // Aggiunte → azzurro
    if (lower.includes('aggiunta')) return 'anagrafiche-badge-log anagrafiche-badge-log-aggiunta';

    // Eliminazioni → rosso
    if (lower.includes('eliminaz')) return 'anagrafiche-badge-log anagrafiche-badge-log-eliminazione';

    // Cambio stato → giallo
    if (lower.includes('cambio stato')) return 'anagrafiche-badge-log anagrafiche-badge-log-cambio-stato';

    // Abilitazioni → viola
    if (lower.includes('abilita')) return 'anagrafiche-badge-log anagrafiche-badge-log-abilitazione';

    return 'anagrafiche-badge-log';
  };

  const getDotColor = (azione: string): string => {
    const lower = azione.toLowerCase();

    if (lower.includes('creaz')) return '#10b981';
    if (lower.includes('modifica dati') || lower.includes('modifica cliente') ||
        lower.includes('modifica fornitore') || lower.includes('modifica indirizzo') ||
        lower.includes('modifica email') || lower.includes('modifica contatto') ||
        lower.includes('modifica filiale')) {
      return '#3b82f6';
    }
    if (lower.includes('aggiunta')) return '#0ea5e9';
    if (lower.includes('eliminaz')) return '#ef4444';
    if (lower.includes('cambio stato')) return '#f59e0b';
    if (lower.includes('abilita')) return '#9333ea';

    return '#94a3b8';
  };

  const renderDettaglio = (dettaglio: string) => {
    // Se il dettaglio contiene ' | ', renderizza ogni segmento su una riga separata
    if (dettaglio.includes(' | ')) {
      const segmenti = dettaglio.split(' | ');
      return (
        <div>
          {segmenti.map((seg, idx) => (
            <div key={idx} className={idx < segmenti.length - 1 ? 'bop-tab-log__detail-segment' : ''}>
              {seg}
            </div>
          ))}
        </div>
      );
    }
    return dettaglio;
  };

  return (
    <div>
      <div className="anagrafiche-card">
        <h3 className="anagrafiche-section-title">Storico Attività</h3>

        {log && log.length > 0 ? (
          <div className="anagrafiche-timeline">
            {log.map((entry, index) => {
              const badgeClass = getAzioneBadgeClass(entry.azione);
              const dotColor = getDotColor(entry.azione);
              const isLast = index === log.length - 1;

              return (
                <div
                  key={entry.id}
                  className={`anagrafiche-timeline-item ${isLast ? 'anagrafiche-timeline-item-last' : ''}`}
                >
                  <div className="anagrafiche-timeline-dot" style={{ background: dotColor }}></div>

                  <div className="anagrafiche-timeline-date">
                    <span>
                      {formatDate(entry.createdAt)}
                    </span>
                  </div>

                  <div className="anagrafiche-timeline-action">
                    <span className={badgeClass}>
                      {entry.azione}
                    </span>
                    <span className="anagrafiche-timeline-user">
                      da <strong className="anagrafiche-timeline-user-name">{entry.utenteName}</strong>
                    </span>
                  </div>

                  {entry.dettaglio && (
                    <div className="anagrafiche-timeline-detail" style={{ borderLeftColor: dotColor }}>
                      {renderDettaglio(entry.dettaglio)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="anagrafiche-empty-state">
            <ClockIcon />
            <p className="anagrafiche-empty-state-title">
              Nessuna attività registrata
            </p>
            <p className="anagrafiche-empty-state-subtitle">
              Lo storico delle modifiche apparirà qui quando verranno effettuate operazioni su questo nominativo.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
